import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { normalizeDocumentType } from '../utils/documentTypes';

/**
 * Cria document_links polimorficos para um attachment recém-criado, sem duplicar.
 *
 * Regra Onda 19 (ADR-009):
 *  - oferta_id  → entity_type='oferta'
 *  - area_id    → entity_type='area'   (bonus: linkagem dupla quando oferta tem area)
 *  - contact_id → entity_type='contato' (consistência com convenção polimórfica)
 *
 * relation_type='source_document' segue o padrão de agent-intake-document.
 * Idempotente por design: checa existência antes de inserir.
 */
const ensureDocumentLinks = async (attachmentId, ctx) => {
  const targets = [];
  if (ctx.oferta_id) targets.push({ entity_type: 'oferta', entity_id: ctx.oferta_id });
  if (ctx.area_id) targets.push({ entity_type: 'area', entity_id: ctx.area_id });
  if (ctx.contact_id) targets.push({ entity_type: 'contato', entity_id: ctx.contact_id });

  for (const t of targets) {
    try {
      const { data: existing, error: qErr } = await supabase
        .from('document_links')
        .select('id')
        .eq('attachment_id', attachmentId)
        .eq('entity_type', t.entity_type)
        .eq('entity_id', t.entity_id)
        .limit(1);
      if (qErr) throw qErr;
      if (existing && existing.length > 0) continue;

      const { error: insErr } = await supabase
        .from('document_links')
        .insert({
          attachment_id: attachmentId,
          entity_type: t.entity_type,
          entity_id: t.entity_id,
          relation_type: 'source_document',
          created_by: ctx.created_by || null,
        });
      if (insErr) throw insErr;
    } catch (err) {
      // Logamos mas não derrubamos o upload — anexo já está em email_attachments,
      // o link é metadado adicional. Re-link pode ser feito via backfill se preciso.
      console.error(`[useAttachments] document_link ${t.entity_type} falhou:`, err);
    }
  }
};

/**
 * Hook de anexos de uma oferta.
 *
 * Carrega anexos vinculados DIRETAMENTE a oferta (manual_upload / drag_drop)
 * + anexos vinculados aos emails da oferta (gmail_sync) — passados via
 * emailIds (array) pelo caller.
 *
 * Uploads passam pela edge function upload-attachment (idempotente por sha256),
 * nao por insert direto — assim a UI compartilha o mesmo canal de dedup e
 * hashing do Apps Script.
 */
export const useAttachments = (ofertaId, emailIds = []) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const emailIdsKey = (emailIds || []).slice().sort().join(',');

  const fetchAll = useCallback(async () => {
    if (!ofertaId && (!emailIds || emailIds.length === 0)) {
      setAttachments([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Um fetch para anexos da oferta (manual/drag_drop) + outro para emails.
      // OR encadeado no PostgREST e fragil com uuid arrays; duas queries sao mais robustas.
      const queries = [];
      if (ofertaId) {
        queries.push(
          supabase
            .from('email_attachments')
            .select('*')
            .eq('oferta_id', ofertaId)
            .order('created_at', { ascending: false })
        );
      }
      if (emailIds && emailIds.length > 0) {
        queries.push(
          supabase
            .from('email_attachments')
            .select('*')
            .in('email_id', emailIds)
            .order('created_at', { ascending: false })
        );
      }
      const results = await Promise.all(queries);
      const merged = [];
      const seen = new Set();
      for (const { data, error: qErr } of results) {
        if (qErr) throw qErr;
        for (const row of (data || [])) {
          if (seen.has(row.id)) continue;
          seen.add(row.id);
          merged.push(row);
        }
      }
      merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAttachments(merged);
    } catch (err) {
      setError(err.message);
      console.error('[useAttachments] fetch falhou:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ofertaId, emailIdsKey]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /**
   * Le File do browser, converte para base64 e chama a edge function.
   *
   * context: {
   *   source?: 'manual_upload' | 'drag_drop',
   *   oferta_id?, empresa_id?, email_id?,
   *   document_type?, description?, area_id?, contact_id?,  // v1.1.0
   *   uploaded_by?,
   * }
   */
  const uploadFile = async (file, context = {}) => {
    if (!file) throw new Error('file obrigatorio');

    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = '';
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
      }
      const fileB64 = btoa(binary);

      const payload = {
        source: context.source || 'manual_upload',
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        file_base64: fileB64,
        oferta_id: context.oferta_id || null,
        empresa_id: context.empresa_id || null,
        email_id: context.email_id || null,
        uploaded_by: context.uploaded_by || null,
        document_type: normalizeDocumentType(context.document_type),
        description: context.description || null,
        area_id: context.area_id || null,
        contact_id: context.contact_id || null,
      };

      console.info('[useAttachments] uploadFile →', {
        filename: file.name, size: file.size, source: payload.source,
        oferta_id: payload.oferta_id, document_type: payload.document_type,
      });

      const { data, error: invErr } = await supabase.functions.invoke('upload-attachment', {
        body: payload,
      });
      if (invErr) {
        console.error('[useAttachments] invoke error:', invErr);
        // FunctionsHttpError carrega a Response em .context
        if (invErr?.context && typeof invErr.context.text === 'function') {
          try {
            const bodyTxt = await invErr.context.text();
            throw new Error(`edge function ${invErr.context.status || '?'}: ${bodyTxt.slice(0, 300)}`);
          } catch { /* ignore parse */ }
        }
        throw invErr;
      }
      if (!data?.ok) {
        console.error('[useAttachments] edge function returned error:', data);
        throw new Error(data?.error || 'upload falhou');
      }

      console.info('[useAttachments] upload OK:', data);

      // Onda 19 (ADR-009) — vínculo polimórfico via document_links.
      // A edge function só preenche email_attachments; cria os links
      // entity_type='oferta' (e 'area' como bonus) aqui no client.
      // Idempotente: checa NOT EXISTS antes de inserir, cobrindo o caso
      // de upload deduplicado (mesmo sha256 → mesmo attachment_id).
      if (data?.attachment_id) {
        await ensureDocumentLinks(data.attachment_id, {
          oferta_id: payload.oferta_id,
          area_id: payload.area_id,
          contact_id: payload.contact_id,
          created_by: payload.uploaded_by,
        });
      }

      await fetchAll();
      return data;
    } catch (err) {
      console.error('[useAttachments] uploadFile falhou:', err);
      throw err;
    }
  };

  /**
   * Download: gera signed URL curta (5min) e baixa via link.
   */
  const downloadAttachment = async (att) => {
    const { data, error: signErr } = await supabase
      .storage
      .from('email-attachments')
      .createSignedUrl(att.storage_path, 300, { download: att.filename });
    if (signErr) throw signErr;
    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    }
  };

  const deleteAttachment = async (attachmentId) => {
    const { data: row, error: eGet } = await supabase
      .from('email_attachments')
      .select('storage_path')
      .eq('id', attachmentId)
      .single();
    if (eGet) throw eGet;

    // Deleta row primeiro (protege contra race). Storage via API se possivel.
    const { error: delErr } = await supabase
      .from('email_attachments')
      .delete()
      .eq('id', attachmentId);
    if (delErr) throw delErr;

    if (row?.storage_path) {
      await supabase.storage.from('email-attachments').remove([row.storage_path]).catch(() => {});
    }
    await fetchAll();
  };

  return { attachments, loading, error, refetch: fetchAll, uploadFile, downloadAttachment, deleteAttachment };
};
